import { useEffect, useRef, useState } from 'react'
import { useSnackbar } from 'notistack'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers'
import { useParams } from 'react-router-dom'
import { useAuth } from 'lib/auth'
import { gtmEvent } from 'lib/gtm'
import { amplitudeEvent } from 'lib/amplitude'
import { COACHING_CENTER_SLUGS } from 'utils/constants'
import { validationSchema } from '../utils'
import { clearGuestBooking } from '../guest-booking'
import { usePrepareLessonForStudent } from './hooks'

// Checkout behavior of the private lesson review step (payment intent
// creation, skip payment, waiver, free / pay-at-location bookings and the
// analytics events). The legacy `LessonReviewLayout` and the redesigned layout
// (`redesign/`) both render from this hook so they cannot drift apart.
const useLessonReviewCheckout = ({
  lessonType,
  locationId,
  startDateTime,
  student,
  isComplete,
  setIsComplete,
  eventObject = {},
  guest = null,
}) => {
  const { priceInCents, acceptsOnlinePayments } = lessonType
  const facility = lessonType.facilities.find(({ id }) => id === locationId)

  const { user } = useAuth()
  const { idOrSlug } = useParams()
  const coachBooksOnBehalfOfStudent =
    user?.externalId === lessonType?.coach.externalId
  const shouldShowWaiver =
    COACHING_CENTER_SLUGS.includes(lessonType?.academy?.slug) &&
    !coachBooksOnBehalfOfStudent
  const [showWaiver, setShowWaiver] = useState(false)
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [showWaiver])

  const canSkipPayment = user && user?.id === lessonType?.coach?.id
  const [paymentRequired, setPaymentRequired] = useState(
    !canSkipPayment && acceptsOnlinePayments,
  )
  const submitButtonLabel = shouldShowWaiver
    ? 'Continue'
    : acceptsOnlinePayments
      ? 'Complete Purchase'
      : 'Complete Booking'

  const bookNewSessionUrl = `/coach/${idOrSlug}/schedule?lessonTypeId=${lessonType?.id}&locationId=${locationId}`

  useEffect(() => {
    amplitudeEvent('view-checkout-screen', {
      payment_required: paymentRequired,
      purchase_type: 'private_booking',
      payment_intent_enabled: true,
      is_guest: !!guest,
      coach_books_on_behalf_of_student: coachBooksOnBehalfOfStudent,
    })
    // guest is fixed for the lifetime of the page
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentRequired])

  // Guests have no user to save the opt-in on, so the prompt is hidden for them
  const userIsNotOptedIn = !guest && !user?.marketingOptInAt
  const [marketingOptInChecked, setMarketingOptInChecked] = useState(false)

  const { enqueueSnackbar } = useSnackbar()
  const stripePaymentFormRef = useRef(null)

  // Delay usePrepareLessonForStudent until the Review step submit button is clicked
  const shouldDelayClientSecretFetch = !paymentRequired
  const [shouldFetchClientSecret, setShouldFetchClientSecret] = useState(
    !shouldDelayClientSecretFetch,
  )
  const [pendingButtonAction, setPendingButtonAction] = useState(null)

  const {
    clientSecret,
    setClientSecret,
    loading: clientSecretLoading,
    error: clientSecretError,
    success: prepareLessonSuccess,
    message: prepareLessonMessage,
  } = usePrepareLessonForStudent({
    lessonTypeId: lessonType.id,
    locationId: facility?.id,
    startDateTime,
    studentId: student.id,
    guest,
    skipPayment: !paymentRequired,
    enabled: shouldFetchClientSecret,
    shouldExcludeSkipPayment:
      !priceInCents || priceInCents === 0 || !acceptsOnlinePayments,
  })

  // When skipPayment is toggled, manage fetching state
  useEffect(() => {
    if (paymentRequired && !shouldFetchClientSecret) {
      setShouldFetchClientSecret(true)
    } else if (!paymentRequired && shouldFetchClientSecret) {
      setShouldFetchClientSecret(false)
      setClientSecret(null)
    }
  }, [paymentRequired, shouldFetchClientSecret, setClientSecret])

  // Proceed with pending action once client secret is available
  useEffect(() => {
    // For skipPayment case, we need loading to complete and check success status
    // For payment required case, we need clientSecret to exist
    const isSkipPaymentCase = shouldDelayClientSecretFetch && !paymentRequired

    // Only proceed if loading is complete and we have a definitive result
    const loadingComplete = !clientSecretLoading
    const hasResult = isSkipPaymentCase
      ? prepareLessonSuccess !== null // For skip payment, we need success status (true or false)
      : !!clientSecret // For payment required, we need clientSecret

    const canProceed = pendingButtonAction && loadingComplete && hasResult

    if (canProceed) {
      const proceedWithAction = async () => {
        // When skipPayment is true and we delayed the fetch, purchase is already handled in prepareLessonForStudentHook
        // Set isComplete to show SummaryLayout instead of redirecting
        if (shouldDelayClientSecretFetch && !paymentRequired) {
          if (prepareLessonSuccess === true) {
            enqueueSnackbar('Your session has been booked.', {
              variant: 'success',
            })
            gtmEvent({
              ...eventObject,
              event: 'complete-private-session-booking',
            })
            amplitudeEvent('complete-checkout', {
              purchase_type: 'private_lesson',
              payment_required: false,
              is_guest: !!guest,
              coach_books_on_behalf_of_student: coachBooksOnBehalfOfStudent,
            })
            clearGuestBooking()
            setIsComplete(!isComplete)
            setPendingButtonAction(null)
          } else if (prepareLessonSuccess === false) {
            const errorMessage =
              prepareLessonMessage ||
              'Failed to book session. Please try again.'
            enqueueSnackbar(errorMessage, {
              variant: 'error',
            })
            setPendingButtonAction(null)
          }
          return
        }

        if (stripePaymentFormRef.current?.validatePayment) {
          const isValid = await stripePaymentFormRef.current.validatePayment()
          if (!isValid) {
            setPendingButtonAction(null)
            return
          }
        }

        if (shouldShowWaiver) {
          setShowWaiver(true)
          setPendingButtonAction(null)
        } else {
          await stripePaymentFormRef.current.submitPayment()
          setPendingButtonAction(null)
        }
      }
      proceedWithAction()
    }
  }, [
    guest,
    coachBooksOnBehalfOfStudent,
    pendingButtonAction,
    clientSecret,
    clientSecretLoading,
    shouldShowWaiver,
    shouldDelayClientSecretFetch,
    paymentRequired,
    isComplete,
    setIsComplete,
    eventObject,
    enqueueSnackbar,
    prepareLessonSuccess,
    prepareLessonMessage,
  ])

  const form = useForm({
    resolver: yupResolver(
      validationSchema(paymentRequired, shouldShowWaiver, true),
    ),
    ...(shouldShowWaiver && {
      defaultValues: {
        agreeWaiver: false,
      },
    }),
  })

  const handleButtonClick = async () => {
    if (stripePaymentFormRef?.current?.validatePayment) {
      const isValid = await stripePaymentFormRef.current.validatePayment()
      if (!isValid) return
    }

    if (shouldShowWaiver) {
      setShowWaiver(true)
      return
    }

    // Trigger prepareLessonForStudent if it was delayed
    if (shouldDelayClientSecretFetch && !shouldFetchClientSecret) {
      setShouldFetchClientSecret(true)
      setPendingButtonAction(true)
      return
    }

    return stripePaymentFormRef?.current?.submitPayment()
  }

  const handleWaiverSubmit = async () => {
    if (paymentRequired) {
      if (stripePaymentFormRef.current?.submitPayment) {
        return stripePaymentFormRef.current.submitPayment()
      }
      return
    }

    setShouldFetchClientSecret(true)
    setPendingButtonAction(true)
  }

  return {
    idOrSlug,
    user,
    facility,
    canSkipPayment,
    coachBooksOnBehalfOfStudent,
    shouldShowWaiver,
    showWaiver,
    setShowWaiver,
    paymentRequired,
    setPaymentRequired,
    submitButtonLabel,
    bookNewSessionUrl,
    userIsNotOptedIn,
    marketingOptInChecked,
    setMarketingOptInChecked,
    stripePaymentFormRef,
    clientSecret,
    clientSecretLoading,
    clientSecretError,
    prepareLessonSuccess,
    prepareLessonMessage,
    pendingButtonAction,
    form,
    handleButtonClick,
    handleWaiverSubmit,
  }
}

export default useLessonReviewCheckout
